import "./Carousel.css";

import { ArrowheadLeftLightIcon, CircleArrowheadLeftDarkIcon, CircleArrowheadRightDarkIcon } from "../../../assets";
import { CarouselActivity } from "../../../models/shared/carouselActivityType";
import { StatusCard } from "../../../templates/DeveloperProjectCardWithTabsTemplate/components";
import { checkCanManageActivity } from "../../../utils";
import { ProgressIndicator } from "./components";
import { useCarousel } from "./useCarousel";

type CarouselVariant = "Default" | "AssetManager";
type CarouselLocation = "Project" | "Activity";

interface CarouselProps {
  status?: string;
  activities: CarouselActivity[];
  canManageActivity: boolean;
  isExternal: boolean;
  location?: CarouselLocation;
  variant?: CarouselVariant;
  shouldRefreshActivities: () => void;
  shouldRefreshProjectDetails?: () => void;
}

export const Carousel = ({
  status,
  activities,
  canManageActivity,
  isExternal,
  variant = "Default",
  location = "Project",
  shouldRefreshActivities,
  shouldRefreshProjectDetails,
}: CarouselProps): JSX.Element => {
  const {
    hasNoSubmitButtonPadding,
    hasNoCompleteButtonPadding,
    areRightActivitiesHidden,
    onRightArrowClick,
    onLeftArrowClick,
    carouselRef,
    activitiesWrapperWidth,
  } = useCarousel();

  return (
    <div className={`CarouselWrapper ${variant}`}>
      <div className="Carousel" ref={carouselRef}>
        {activities.length === 2 && !areRightActivitiesHidden && (
          <ArrowheadLeftLightIcon className="ArrowheadLeftLightIcon" onClick={onLeftArrowClick} />
        )}
        {activities.length === 3 && !areRightActivitiesHidden && (
          <CircleArrowheadLeftDarkIcon className="CircleArrowheadLeftDarkIcon" onClick={onLeftArrowClick} />
        )}
        <div className="CarouselStatus">
          {status !== undefined && <StatusCard status={status} isFixed variant={variant} />}
        </div>
        <div
          className={`ActivitiesWrapper ${activities.length < 2 && "Full"}`}
          style={{ width: activitiesWrapperWidth }}
        >
          {variant === "Default" ? (
            <h3 className="ActivitiesHeader">Activities</h3>
          ) : (
            <p className="body01 ActivitiesHeader">Activities</p>
          )}
          {activities.length < 2 ? (
            <div className="Activities">
              <ProgressIndicator
                activity={activities[0]}
                canManageActivity={checkCanManageActivity(canManageActivity, activities[0]?.status)}
                isExternal={isExternal}
                hasNoSubmitButtonPadding={hasNoSubmitButtonPadding}
                hasNoCompleteButtonPadding={hasNoCompleteButtonPadding}
                location={location}
                shouldRefreshActivities={shouldRefreshActivities}
                shouldRefreshProjectDetails={shouldRefreshProjectDetails}
              />
            </div>
          ) : (
            <div className="Activities">
              {activities.map((activity, idx) => (
                <ProgressIndicator
                  key={activity.displayName}
                  activity={activity}
                  canManageActivity={checkCanManageActivity(canManageActivity, activity.status)}
                  isExternal={isExternal}
                  nrOfActivities={activities.length}
                  isDisabled={
                    (activities.length > 2 && idx === 0 && !areRightActivitiesHidden) ||
                    (idx === 1 && areRightActivitiesHidden)
                  }
                  hasNoSubmitButtonPadding={hasNoSubmitButtonPadding}
                  hasNoCompleteButtonPadding={hasNoCompleteButtonPadding}
                  location={location}
                  shouldRefreshActivities={shouldRefreshActivities}
                  shouldRefreshProjectDetails={shouldRefreshProjectDetails}
                />
              ))}
              {areRightActivitiesHidden ? (
                <CircleArrowheadRightDarkIcon
                  className="CircleArrowheadRightDarkIcon Enabled"
                  onClick={onRightArrowClick}
                />
              ) : (
                <CircleArrowheadRightDarkIcon
                  className="CircleArrowheadRightDarkIcon Disabled"
                  onClick={onRightArrowClick}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
